import { useState } from "react";
import { Copy, Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { EMAILS, FILES } from "@/components/outreach/outreachContent";
import { WorkshopsManager } from "@/components/outreach/WorkshopsManager";
import { TopicPlanner } from "@/components/outreach/TopicPlanner";
import { LibraryReports } from "@/components/outreach/LibraryReports";
import { WorkshopContacts } from "@/components/outreach/WorkshopContacts";

function EmailCard({ e, lang }) {
  const copy = (txt, what) => navigator.clipboard.writeText(txt).then(() => toast.success(`${what} copied`));
  const id = `${e.id}${lang === "es" ? "-es" : ""}`;
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 min-w-0" data-testid={`outreach-email-${id}`}>
      <div className="flex flex-wrap items-center gap-2 justify-between"><h3 className="font-semibold text-slate-900">{e.title}</h3>
        <div className="flex gap-2"><Button size="sm" variant="outline" onClick={() => copy(e.subject, "Subject")} data-testid={`outreach-copy-subject-${id}`}>Copy subject</Button>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => copy(e.body, "Email")} data-testid={`outreach-copy-body-${id}`}><Copy className="h-4 w-4 mr-1" />Copy email</Button></div></div>
      <p className="text-sm text-slate-700 mt-3 break-words"><span className="text-slate-500">Subject: </span>{e.subject}</p>
      <pre className="mt-3 whitespace-pre-wrap break-words font-sans text-sm text-slate-700 bg-slate-50 border border-slate-100 rounded p-4 max-h-80 overflow-y-auto">{e.body}</pre>
      <p className="text-xs text-slate-400 mt-2">Replace the [bracketed] names before sending.</p>
    </div>
  );
}

const KIT = [
  ["Workshop slides (English, PowerPoint)", "grantverify-workshop-slides-en.pptx"], ["Workshop slides (Spanish, PowerPoint)", "grantverify-workshop-slides-es.pptx"],
  ["Slides PDF (English)", "grantverify-workshop-slides-en.pdf"], ["Slides PDF (Spanish)", "grantverify-workshop-slides-es.pdf"],
  ["Sign-in sheet (English)", "grantverify-workshop-signin-en.pdf"], ["Sign-in sheet (Spanish)", "grantverify-workshop-signin-es.pdf"],
];
const link = "inline-flex items-center gap-1.5 text-sm border border-slate-200 rounded-md px-3 py-1.5 hover:bg-slate-50 transition-colors";

function WorkshopKit() {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 mb-4" data-testid="workshop-kit">
      <h3 className="font-semibold text-slate-900">Library workshop kit</h3>
      <p className="text-xs text-slate-500 mt-1 mb-3">A 10-slide, 30-minute "Finding Grants for Your Business" talk with speaker notes under each slide. Opens in PowerPoint, Google Slides or Keynote. Replace the [bracketed] library name, date and presenter on slide 1.</p>
      <div className="flex flex-wrap gap-2">{KIT.map(([l, f]) => <a key={f} href={`/brand/workshop/${f}`} download className={link} data-testid={`workshop-download-${f.replace(".", "-")}`}><Download className="h-4 w-4 text-emerald-600" />{l}</a>)}</div>
    </div>
  );
}

export function OutreachEmails() {
  const [lang, setLang] = useState("en");
  const [workshops, setWorkshops] = useState([]);
  const [rk, setRk] = useState(0);
  return (
    <div data-testid="outreach-emails">
      <WorkshopKit />
      <WorkshopsManager onChange={setWorkshops} reloadKey={rk} />
      <TopicPlanner onDrafted={() => setRk((k) => k + 1)} />
      <LibraryReports reloadKey={workshops.map((w) => w.status).join()} />
      <WorkshopContacts workshops={workshops} />
      <div className="bg-white border border-slate-200 rounded-lg p-5 mb-4" data-testid="print-downloads">
        <h3 className="font-semibold text-slate-900 mb-3">Print-ready handouts</h3>
        <div className="flex flex-wrap gap-2">{FILES.map(([l, f]) => <a key={f} href={`/brand/print/${f}`} download className={link} data-testid={`print-download-${f.replace(".pdf", "")}`}><Download className="h-4 w-4 text-emerald-600" />{l}</a>)}</div>
        <p className="text-xs text-slate-500 mt-3">Table tent: print on letter card stock, fold along the dashed line and stand it up. English faces one side, Spanish the other.</p>
      </div>
      <div className="flex gap-2 mb-3" data-testid="outreach-lang-toggle">
        {[["en", "English emails"], ["es", "Spanish emails"]].map(([k, l]) => (
          <button key={k} onClick={() => setLang(k)} className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${lang === k ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`} data-testid={`outreach-lang-${k}`}>{l}</button>))}
      </div>
      <div className="grid lg:grid-cols-2 gap-4">{EMAILS[lang].map((e) => <EmailCard key={`${lang}-${e.id}`} e={e} lang={lang} />)}</div>
    </div>
  );
}
